/** day-pay-policies' template status. */
export enum TemplateStatus {
  Draft = 0,
  Published = 1,
  Deprecated = 2
}

export const STATUS_LABELS: Record<TemplateStatus, string> = {
  [TemplateStatus.Draft]: 'Draft',
  [TemplateStatus.Published]: 'Published',
  [TemplateStatus.Deprecated]: 'Deprecated'
};

export const STATUS_COLORS: Record<TemplateStatus, 'default' | 'success' | 'warning'> = {
  [TemplateStatus.Draft]: 'default',
  [TemplateStatus.Published]: 'success',
  [TemplateStatus.Deprecated]: 'warning'
};

export interface TemplatePlugin {
  pluginId: string;
  /** An exact version or a package.json-style range, e.g. ^1.0.0. */
  version: string;
}

/** One single-key object per param, as day-pay-policies stores them: [{ limit: 10 }, { scope: 'punch' }]. */
export interface TemplateDefaultParams {
  pluginId: string;
  params: Record<string, unknown>[];
}

export interface PayPolicyTemplate {
  uuid: string;
  name: string;
  version: number;
  /** Shared by every version of a template. */
  familyUuid: string;
  description: string;
  status: TemplateStatus;
  companyUuids: string[] | null;
  plugins: TemplatePlugin[];
  defaultParams: TemplateDefaultParams[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateContent {
  name: string;
  description: string;
  status: TemplateStatus;
  companyUuids: string[];
  plugins: TemplatePlugin[];
  defaultParams: TemplateDefaultParams[];
}

export interface CursorPage<T> {
  content: T[];
  /** `total` counts every match across pages, whatever the cursor. */
  metadata: { limit: number; nextCursor?: string | null; total?: number };
}

export interface CatalogPlugin {
  id: string;
  versions: string[];
  family: string;
}

export interface FieldCondition {
  field?: string;
  is?: unknown;
  isNot?: unknown;
  in?: string[];
  exceeds?: number;
  plugin?: string;
  present?: boolean;
}

export interface ObjectFieldSchema {
  name: string;
  kind: 'number' | 'boolean' | 'string';
  options?: string[];
  validation: FieldValidation;
}

export interface FieldValidation {
  required: boolean;
  min?: number;
  max?: number;
  pattern?: string;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  items?: { kind: 'number' | 'boolean' | 'string' | 'object'; min?: number; max?: number; options?: string[]; fields?: ObjectFieldSchema[] };
  activeWhen?: FieldCondition;
  /** When present, the param is required only while this holds. */
  requiredWhen?: FieldCondition;
}

/** One param of a plugin: `type` picks the control, `kind` the JSON type of its value. */
export interface PluginField {
  name: string;
  type: string;
  kind: 'number' | 'boolean' | 'string' | 'array';
  default: unknown;
  options?: string[];
  validation: FieldValidation;
}

export interface PluginDependency {
  plugin: string;
  version: string;
  /** An optional producer runs only when the template roots it or another plugin requires it. */
  optional?: boolean;
}

export interface PluginSchema {
  id: string;
  version: string;
  requestedVersion: string;
  family: string;
  fields: PluginField[] | null;
  dependencies: PluginDependency[] | null;
}
